import * as React from 'react';
import Papa from 'papaparse';
import { Loader } from 'Src/components/Loader/Loader';
import { superAdminPost } from 'Src/helpers/service/superadmin/api';
import { getSuperAdminSchools } from 'Src/helpers/service/superadmin/schools';
import {
  setSuperAdminUserEmailVerification,
  setSuperAdminUserSmsVerification
} from 'Src/helpers/service/superadmin/users';

// ─── Helpers (same as parents) ────────────────────────────────────────────────

const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEXP = /\d/;
const ALLOW_ROLES = ['ADMIN', 'MANAGER', 'TEACHER', 'COACH', 'GOVERNOR'];

function randomStr() {
  return Math.random()
    .toString(36)
    .substring(2, 10);
}

function formatPhone(phone: string): string | undefined {
  if (!phone || !PHONE_REGEXP.test(phone)) return undefined;
  let p = phone.replace(/[\s+]/g, '');
  if (p.startsWith('0')) p = '44' + p.slice(1);
  if (p.startsWith('440')) p = '44' + p.slice(3);
  if (!p.startsWith('44')) p = '44' + p;
  return '+' + p;
}

function getEmail(raw: string, wondeUPI: string): string {
  if (raw && raw.trim() && EMAIL_REGEXP.test(raw.trim())) return raw.trim();
  return `NOTSEND_${wondeUPI || randomStr()}@squadintouch.com`;
}

function getPhone(mobile: string, primary: string): string | undefined {
  return formatPhone(mobile) || formatPhone(primary);
}

function parseDOB(val: string): string | undefined {
  if (!val || val.trim() === '') return undefined;
  const parts = val.split(/[\/\-]/);
  if (parts.length === 3 && parts[2].length === 4) {
    const m = parts[1].length < 2 ? '0' + parts[1] : parts[1];
    const d = parts[0].length < 2 ? '0' + parts[0] : parts[0];
    return `${parts[2]}-${m}-${d}`;
  }
  return undefined;
}

interface ParseError {
  row: number;
  message: string;
}

interface StaffRow {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  gender?: string;
  birthday?: string;
  misId?: string;
  role: string;
}

function parseStaffCSV(file: File): Promise<{ users: StaffRow[]; errors: ParseError[] }> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: result => {
        const errors: ParseError[] = result.errors.map(e => ({ row: e.row || 0, message: e.message }));
        const rows = result.data as any[];
        const fields = result.meta.fields || [];

        const col = (row: any, ...keys: string[]) => {
          for (const k of keys) {
            const f = fields.find(f => f.toLowerCase() === k.toLowerCase());
            if (f && row[f] !== undefined && row[f] !== '') return row[f];
          }
          return '';
        };

        const users: StaffRow[] = rows
          .filter(r => Object.keys(r).length > 1)
          .map((row, i) => {
            const role = col(row, 'Role', 'role').toUpperCase();
            if (role && ALLOW_ROLES.indexOf(role) === -1) {
              errors.push({
                row: i + 1,
                message: `${col(row, 'firstName')} ${col(
                  row,
                  'lastName'
                )}: invalid role "${role}". Allowed: ${ALLOW_ROLES.join(', ')}`
              });
            }
            const wondeUPI = col(row, 'wondeUPI');
            const gender = col(row, 'gender').toLowerCase();
            return {
              firstName: col(row, 'firstName').trim(),
              lastName: col(row, 'lastName').trim(),
              email: getEmail(col(row, 'email'), wondeUPI),
              phone: getPhone(col(row, 'mobilePhone'), col(row, 'primaryPhone')),
              gender:
                gender === 'male' || gender === '1'
                  ? 'MALE'
                  : gender === 'female' || gender === '0'
                  ? 'FEMALE'
                  : undefined,
              birthday: parseDOB(col(row, 'DOB')),
              misId: col(row, 'misId', 'mis_id') || undefined,
              role: role || 'TEACHER'
            };
          })
          .filter(u => u.firstName || u.lastName);

        // Check duplicates
        const emails = new Set<string>();
        const phones = new Set<string>();
        users.forEach((u, i) => {
          if (!u.email.startsWith('NOTSEND_')) {
            if (emails.has(u.email)) errors.push({ row: i, message: `Duplicate email: ${u.email}` });
            else emails.add(u.email);
          }
          if (u.phone && !u.phone.startsWith('NOTSEND_')) {
            if (phones.has(u.phone)) errors.push({ row: i, message: `Duplicate phone: ${u.phone}` });
            else phones.add(u.phone);
          }
        });

        resolve({ users, errors });
      },
      error: err => reject(err)
    });
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {}
interface State {
  schoolSearch: string;
  schoolId: string;
  schoolName: string;
  schoolOptions: Array<{ id: string; name: string }>;
  isSearchingSchools: boolean;
  users: StaffRow[];
  parseErrors: ParseError[];
  isUploading: boolean;
  uploadProgress: number;
  uploadTotal: number;
  uploadErrors: string[];
  successMessage: string;
  errorMessage: string;
  importAsBlocked: boolean;
}

export class SuperAdminImportStaff extends React.Component<Props, State> {
  schoolSearchTimer: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      schoolSearch: '',
      schoolId: '',
      schoolName: '',
      schoolOptions: [],
      isSearchingSchools: false,
      users: [],
      parseErrors: [],
      isUploading: false,
      uploadProgress: 0,
      uploadTotal: 0,
      uploadErrors: [],
      successMessage: '',
      errorMessage: '',
      importAsBlocked: false
    };
  }

  onSchoolSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    this.setState({ schoolSearch: val, schoolId: '', users: [], parseErrors: [], successMessage: '' });
    clearTimeout(this.schoolSearchTimer);
    if (val.trim().length < 2) {
      this.setState({ schoolOptions: [] });
      return;
    }
    this.schoolSearchTimer = setTimeout(() => {
      this.setState({ isSearchingSchools: true });
      getSuperAdminSchools({ where: { name: { like: val, options: 'i' } }, limit: 10, skip: 0 })
        .then(schools =>
          this.setState({
            isSearchingSchools: false,
            schoolOptions: schools.map(s => ({ id: s.id, name: s.name || '' }))
          })
        )
        .catch(() => this.setState({ isSearchingSchools: false }));
    }, 300);
  };

  onSchoolSelect = (school: { id: string; name: string }) => {
    this.setState({
      schoolId: school.id,
      schoolName: school.name,
      schoolSearch: school.name,
      schoolOptions: [],
      users: [],
      parseErrors: []
    });
  };

  onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    parseStaffCSV(file).then(({ users, errors }) => {
      this.setState({ users, parseErrors: errors, successMessage: '', uploadErrors: [] });
    });
  };

  retry = (fn: () => Promise<any>, times = 5): Promise<any> => {
    let p: Promise<any> = Promise.reject(new Error('start'));
    for (let i = 0; i < times; i++) {
      p = p.catch(() => fn()).catch(() => new Promise((_, rej) => setTimeout(rej, 1000)));
    }
    return p;
  };

  onUpload = async () => {
    const { schoolId, users, importAsBlocked } = this.state;
    this.setState({
      isUploading: true,
      uploadProgress: 0,
      uploadTotal: users.length,
      uploadErrors: [],
      successMessage: '',
      errorMessage: ''
    });

    let progress = 0;
    const uploadErrors: string[] = [];

    for (const user of users) {
      try {
        const payload: any = {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          status: importAsBlocked ? 'BLOCKED' : 'ACTIVE',
          password: `123qweQWE${randomStr()}`
        };
        if (user.phone) payload.phone = user.phone;
        if (user.gender) payload.gender = user.gender;
        if (user.birthday) payload.birthday = user.birthday;
        if (user.misId) payload.misId = user.misId;

        const createdUser: any = await this.retry(() => superAdminPost('/users', payload));

        if (!importAsBlocked) {
          await this.retry(() => setSuperAdminUserEmailVerification(createdUser.id, true));
          await this.retry(() => setSuperAdminUserSmsVerification(createdUser.id, true));
        }

        await this.retry(() =>
          superAdminPost(`/users/${createdUser.id}/permissions`, {
            preset: user.role,
            schoolId,
            status: 'ACTIVE',
            activatedAt: new Date().toISOString()
          })
        );
      } catch (err) {
        uploadErrors.push(`${user.firstName} ${user.lastName} (${user.email}): failed`);
      }
      progress++;
      this.setState({ uploadProgress: progress });
    }

    this.setState({
      isUploading: false,
      uploadErrors,
      successMessage:
        uploadErrors.length === 0
          ? `${users.length} staff member${users.length !== 1 ? 's' : ''} imported successfully.`
          : `Import finished with ${uploadErrors.length} error${uploadErrors.length !== 1 ? 's' : ''}.`
    });
  };

  render() {
    const {
      schoolSearch,
      schoolId,
      schoolName,
      schoolOptions,
      isSearchingSchools,
      users,
      parseErrors,
      isUploading,
      uploadProgress,
      uploadTotal,
      uploadErrors,
      successMessage,
      errorMessage,
      importAsBlocked
    } = this.state;

    const roleErrors = parseErrors.filter(e => e.message.includes('invalid role'));
    const canUpload = users.length > 0 && !isUploading && roleErrors.length === 0;

    return (
      <div>
        <div className="mb-4">
          <h4 className="mb-1">Import staff</h4>
          <div className="text-muted small">
            Upload a CSV with columns: <code>firstName</code>, <code>lastName</code>, <code>email</code>,
            <code>mobilePhone</code>, <code>primaryPhone</code>, <code>gender</code>, <code>DOB</code>,<code>Role</code>{' '}
            ({ALLOW_ROLES.join(', ')}), <code>misId</code>.
          </div>
        </div>

        {/* School picker */}
        <div className="form-group" style={{ maxWidth: 420, position: 'relative' }}>
          <label className="small font-weight-bold">School</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={schoolSearch}
            onChange={this.onSchoolSearchChange}
            placeholder="Type to search school…"
          />
          {schoolId && <small className="text-success d-block mt-1">✓ {schoolName}</small>}
          {(schoolOptions.length > 0 || isSearchingSchools) && (
            <div
              style={{
                position: 'absolute',
                zIndex: 100,
                background: '#fff',
                border: '1px solid #ced4da',
                borderRadius: 4,
                width: '100%',
                maxHeight: 200,
                overflowY: 'auto'
              }}
            >
              {isSearchingSchools && <div className="p-2 text-muted small">Searching…</div>}
              {schoolOptions.map(s => (
                <div
                  key={s.id}
                  className="px-3 py-2 small"
                  style={{ cursor: 'pointer' }}
                  onMouseDown={() => this.onSchoolSelect(s)}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-check mb-3">
          <input
            type="checkbox"
            className="form-check-input"
            id="staffImportAsBlocked"
            checked={importAsBlocked}
            onChange={e => this.setState({ importAsBlocked: e.target.checked })}
          />
          <label className="form-check-label small" htmlFor="staffImportAsBlocked">
            Import users as blocked
          </label>
        </div>

        {schoolId && (
          <>
            <div className="form-group" style={{ maxWidth: 420 }}>
              <label className="small font-weight-bold">CSV file</label>
              <input type="file" className="form-control-file" accept=".csv" onChange={this.onFileChange} />
            </div>
          </>
        )}

        {parseErrors.length > 0 && (
          <div className={`alert ${roleErrors.length > 0 ? 'alert-danger' : 'alert-warning'}`}>
            <strong>
              {parseErrors.length} issue{parseErrors.length !== 1 ? 's' : ''} found:
            </strong>
            <ul className="mb-0 mt-1" style={{ maxHeight: 150, overflowY: 'auto' }}>
              {parseErrors.map((e, i) => (
                <li key={i}>
                  Row {e.row}: {e.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {users.length > 0 && (
          <div className="mb-3">
            <div className="text-muted small mb-2">
              Ready to import <strong>{users.length}</strong> staff member{users.length !== 1 ? 's' : ''}.
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
              <table className="table table-sm table-bordered mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>MIS ID</th>
                  </tr>
                </thead>
                <tbody>
                  {users.slice(0, 20).map((u, i) => (
                    <tr key={i}>
                      <td className="small">
                        {u.firstName} {u.lastName}
                      </td>
                      <td className="small" style={{ fontSize: 11 }}>
                        {u.email}
                      </td>
                      <td>
                        <span className={`badge ${ALLOW_ROLES.includes(u.role) ? 'badge-info' : 'badge-danger'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="small">{u.misId || '—'}</td>
                    </tr>
                  ))}
                  {users.length > 20 && (
                    <tr>
                      <td colSpan={4} className="text-muted small text-center">
                        … and {users.length - 20} more
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isUploading && (
          <div className="mb-3">
            <div className="small mb-1">
              Uploading {uploadProgress} / {uploadTotal}…
            </div>
            <div className="progress" style={{ height: 8 }}>
              <div
                className="progress-bar"
                style={{ width: `${uploadTotal > 0 ? (uploadProgress / uploadTotal) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {uploadErrors.length > 0 && (
          <div className="alert alert-danger">
            <strong>Upload errors:</strong>
            <ul className="mb-0 mt-1">
              {uploadErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        {canUpload && (
          <button className="btn btn-primary btn-sm" onClick={this.onUpload}>
            Import {users.length} staff member{users.length !== 1 ? 's' : ''}
          </button>
        )}
      </div>
    );
  }
}

